/**
 * iOS Safari (and iOS-embedded WebViews) require audio playback to
 * originate from a user gesture — the very first time. Even the muted
 * `<audio>.play() → pause()` prime we already run isn't always enough,
 * because iOS gates the entire *tab's* audio subsystem behind a real
 * AudioContext that has been created AND `.resume()`'d inside a tap.
 *
 * Once the tab has one AudioContext with `state === 'running'`, every
 * subsequent `<audio>.play()` in that tab is authorised — including
 * ones deferred behind an `await`. So the fix is:
 *
 *   1. Detect iOS user agents (Safari + WebView both need this).
 *   2. On the very first "Pray Now Together" tap, synchronously —
 *      BEFORE any await — call `unlockIOSAudioSync()`.
 *   3. That function creates a single window-scoped AudioContext,
 *      calls `.resume()`, and plays a 1-sample silent buffer through
 *      it. iOS marks the tab as "user granted audio" and never asks
 *      again for the lifetime of the tab.
 *   4. We flag `sessionStorage['pnt.ios.audio.unlocked'] = '1'` so
 *      subsequent taps skip the work.
 *
 * This is idempotent and free on non-iOS platforms (returns instantly).
 *
 * IMPORTANT: this function MUST be called synchronously inside the
 * click / touchend handler. Any `await` before it burns the user
 * gesture window and iOS will silently reject the AudioContext.resume().
 * The `handlePrayNow` implementation calls this on the first line of
 * the tap handler for exactly this reason.
 */

// Broad UA-based iOS detection. Covers:
//   • iPhone / iPod / iPad Safari
//   • iPadOS (which lies and reports macOS UA — sniff `maxTouchPoints`)
//   • Any iOS WebView (SFSafariViewController, WKWebView)
// Kept intentionally lax — false positives (a Mac with a touch screen)
// cost us one no-op AudioContext creation, false negatives cost us the
// entire audio experience.
function isIOSLike() {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
  const ua = String(navigator.userAgent || '');
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  // iPadOS 13+ masquerades as Macintosh. Distinguish by touch.
  const isMacTouch = /Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1;
  return isMacTouch;
}

// Module-scoped so we reuse the same AudioContext across every tap
// in the tab lifetime. iOS gets confused if you create multiple contexts
// per gesture — the second one comes up in `suspended` state and never
// resumes cleanly.
let sharedCtx = null;
const SESSION_FLAG = 'pnt.ios.audio.unlocked';

/**
 * Synchronous unlock. Call this on the first line of a click / tap
 * handler — no awaits, no promises before it.
 *
 * Returns one of:
 *   'skipped'     — non-iOS or already unlocked this tab
 *   'unlocked'    — just ran the unlock successfully
 *   'unavailable' — iOS but no AudioContext support (extremely rare)
 *   'failed'      — the unlock was attempted but resume rejected
 *
 * The return value is exposed mainly so callers can push a telemetry
 * ping — the audio pipeline itself doesn't need to branch on it.
 */
export function unlockIOSAudioSync() {
  if (typeof window === 'undefined') return 'skipped';
  if (!isIOSLike()) return 'skipped';

  // Already unlocked this tab? Skip the work.
  try {
    if (window.sessionStorage && window.sessionStorage.getItem(SESSION_FLAG) === '1') {
      return 'skipped';
    }
  } catch { /* sessionStorage can throw in Safari private mode — proceed */ }

  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return 'unavailable';

  try {
    if (!sharedCtx) {
      sharedCtx = new Ctor();
    }
    // `resume()` is what actually gates the unlock. On iOS it MUST run
    // during a user gesture — that's why this helper exists.
    // The returned promise resolves once the context is running.
    const resumePromise = sharedCtx.resume && sharedCtx.resume();

    // Play a 1-sample silent buffer through the context. This second
    // step is what makes iOS treat the tab as "user consented to audio"
    // for all future <audio> elements — the resume alone doesn't
    // always propagate to the HTMLMediaElement subsystem.
    try {
      const buffer = sharedCtx.createBuffer(1, 1, 22050);
      const src = sharedCtx.createBufferSource();
      src.buffer = buffer;
      src.connect(sharedCtx.destination);
      // `start(0)` is deprecated on some old iOS but still supported;
      // `noteOn(0)` is the legacy fallback. Try both.
      if (typeof src.start === 'function') src.start(0);
      else if (typeof src.noteOn === 'function') src.noteOn(0);
    } catch {
      // Buffer play may not be strictly necessary post-resume on modern
      // iOS — the resume itself usually suffices. Ignore & continue.
    }

    // Mark the tab as unlocked. If sessionStorage throws (Safari
    // private mode) we simply run the unlock again next tap — harmless.
    try { window.sessionStorage && window.sessionStorage.setItem(SESSION_FLAG, '1'); } catch { /* ignore */ }

    // Chain a debug listener on the resume promise for telemetry, but
    // don't block — return synchronously so the caller can proceed
    // straight into `<audio>.play()` while the resume is completing.
    // iOS honours the pending resume for downstream media calls made
    // inside the same gesture.
    if (resumePromise && typeof resumePromise.catch === 'function') {
      resumePromise.catch(() => { /* async failures surface via handleAudioFailure downstream */ });
    }
    return 'unlocked';
  } catch (err) {
    // Very rare — context creation itself threw. Reset so a later tap
    // can try again with a fresh context.
    sharedCtx = null;
    try { window.sessionStorage && window.sessionStorage.removeItem(SESSION_FLAG); } catch { /* ignore */ }
    return 'failed';
  }
}

/**
 * Cheap probe — has the tab been unlocked in this session? Used only
 * for telemetry / debug UI. Never gates real audio decisions.
 */
export function isIOSAudioUnlocked() {
  if (typeof window === 'undefined' || !window.sessionStorage) return false;
  try { return window.sessionStorage.getItem(SESSION_FLAG) === '1'; }
  catch { return false; }
}

/**
 * True when we should try to unlock — used by callers to decide
 * whether to include the iOS phase in telemetry. Cheap and safe to
 * call on any platform.
 */
export function needsIOSUnlock() {
  return isIOSLike() && !isIOSAudioUnlocked();
}
